import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

export default function NotificationBell() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = () => api.get("/applicator/notifications").then(({ data }) => setItems(data || [])).catch(() => {});
  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const unread = items.filter((n) => !n.read).length;

  const openItem = async (n) => {
    if (!n.read) { await api.post(`/applicator/notifications/${n.id}/read`).catch(() => {}); }
    setOpen(false);
    load();
    if (n.link) navigate(n.link);
  };
  const readAll = async () => { await api.post("/applicator/notifications/read-all").catch(() => {}); load(); };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} data-testid="notification-bell" className="relative text-gray-400 hover:text-white p-1">
        <Bell size={19} />
        {unread > 0 && (
          <span data-testid="notification-badge" className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-[#C9A45C] text-black text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notification-dropdown" className="absolute right-0 mt-2 w-[320px] max-h-[70vh] overflow-y-auto bg-[#0C0C0C] border border-[#C9A45C]/25 shadow-2xl z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <span className="errah-label gold-text">{t("notifications")}</span>
            {unread > 0 && (
              <button onClick={readAll} data-testid="mark-all-read" className="text-[11px] text-gray-400 hover:text-[#C9A45C] inline-flex items-center gap-1"><CheckCheck size={13} /> {t("mark_all_read")}</button>
            )}
          </div>
          {items.length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-500 text-center">{t("no_notifications")}</p>
          ) : items.slice(0, 30).map((n) => (
            <button key={n.id} onClick={() => openItem(n)} data-testid={`notif-item-${n.kind}`}
              className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/[0.04] transition-colors ${n.read ? "opacity-60" : "bg-[#C9A45C]/[0.06]"}`}>
              <div className="flex items-start gap-2">
                {!n.read && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#C9A45C] shrink-0" />}
                <div className={n.read ? "pl-3.5" : ""}>
                  <p className="text-[13px] text-gray-200 leading-snug">{n.text}</p>
                  <p className="text-[10px] text-gray-600 mt-1">{(n.created_at || "").slice(0, 16).replace("T", " ")}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
